import { StrictMode, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { HelmetProvider } from 'react-helmet-async'
import { Capacitor } from '@capacitor/core'
import './index.css'
import App from './App.jsx'
import { AppErrorFallback } from './components/ErrorFallback.jsx'
import { Analytics } from '@vercel/analytics/react'
import { initSentry, Sentry } from './lib/sentry'

// Route-based code splitting: SharedNoteView is a separate public route
const SharedNoteView = lazy(() => import('./components/SharedNoteView.jsx'))

// Initialize Sentry BEFORE React renders (catches initialization errors)
initSentry();

// Determine router basename based on platform
// - Web deployment: / (served at app.promptnoteapp.com)
// - Capacitor native: / (served from https://localhost/)
const isNative = Capacitor.isNativePlatform();
const basename = '/';

console.log('[Main] Platform:', isNative ? 'Native (Capacitor)' : 'Web');
console.log('[Main] Router basename:', basename);

// Initialize mobile sync for Capacitor native apps (non-blocking)
// This sets up the bridge between WebView and native keyboard
if (isNative) {
  import('./lib/mobileSync').then(({ initializeMobileSync }) => {
    initializeMobileSync().catch(err => {
      console.error('[Main] Mobile sync initialization error:', err);
    });
  }).catch(err => {
    console.error('[Main] Failed to load mobileSync:', err);
  });
}

// Render the app
createRoot(document.getElementById('root'), {
  // React 19 error hooks — forward to Sentry
  onCaughtError(error, errorInfo) {
    console.error('[React] Caught by ErrorBoundary:', error, errorInfo.componentStack);
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  },
  onUncaughtError(error, errorInfo) {
    console.error('[React] Uncaught error:', error, errorInfo.componentStack);
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  },
}).render(
  <StrictMode>
    <ErrorBoundary FallbackComponent={AppErrorFallback} onError={(error, info) => {
      console.error('[ErrorBoundary] Global:', error, info.componentStack);
      Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
    }}>
      <HelmetProvider>
        <Analytics />
        <BrowserRouter basename={basename}>
          <Routes>
            {/* Public shared note view - lazy loaded, no auth required */}
            <Route path="/shared/note/:token" element={
              <Suspense fallback={
                <div className="flex h-dvh w-full items-center justify-center bg-background text-foreground">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              }>
                <SharedNoteView />
              </Suspense>
            } />
            {/* Main app - all other routes */}
            <Route path="*" element={<App />} />
          </Routes>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  </StrictMode>,
)

// Register PWA Service Worker with auto-update
// Only on web - skip on native Capacitor platforms to avoid conflicts
if (!isNative) {
  import('virtual:pwa-register').then(({ registerSW }) => {
    const updateSW = registerSW({
      onNeedRefresh() {
        // New content available - auto update without prompting
        console.log('New version available, updating...');
        updateSW(true);
      },
      onOfflineReady() {
        console.log('App ready to work offline');
      },
      onRegistered(registration) {
        console.log('SW registered:', registration?.scope);

        // Check for updates every 5 minutes when online
        if (registration) {
          setInterval(() => {
            registration.update();
          }, 5 * 60 * 1000);
        }
      },
      onRegisterError(error) {
        console.error('SW registration failed:', error);
      }
    });
  }).catch(err => {
    console.error('[Main] Failed to register SW:', err);
  });
} else {
  console.log('[Main] Skipping SW registration on native platform');
}
